import { Box, Button, CircularProgress, Stack } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import PublicIcon from "@mui/icons-material/Public";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FormSection from "../../Forms/FormSection/form-section";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { produce } from "immer";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { supportService, userService } from "../../../Core/Api/services";
import { useAppSelector } from "../../../Redux/redux-store";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import UploadCard, { UploadContext } from "../../Cards/UploadCard/upload-card";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { FormDataZendeskTicketRequest } from "../../../Core/Api/Types/form-data-ticket-zendesk-request";
import { IssuePriority } from "../../../Core/Api/Types/issue-priority";
import { useTranslation } from "react-i18next";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { IsEmail } from "../../../Utils/helpers";

export type ModalSupportZendeskTicketElementProps = {};

export type ModalSupportZendeskTicketProps = CommonModalProps<ModalSupportZendeskTicketElementProps>;

export interface Item {
  id: number;
  key: string;
  value: string;
}

const ModalSupportZendeskTicket = React.forwardRef<any, ModalSupportZendeskTicketProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, ...elementProps } = props;
  const customSnackbar = useCustomSnackbar();

  const formId = "ticket";
  const isValid = useAppSelector(GetFormIsValid(formId));
  const [fileData, setFileData] = React.useState<File>();
  const [typologyValue, setTypologyValue] = useState<Item | null>(null);
  const [subTypologyOptions, setSubTypologyOptions] = useState<Item[] | null>(null);
  const [subTypologyValue, setSubTypologyValue] = useState<Item | null>(null);
  const [jsonObject, setJsonObject] = useState<any>(null);
  const [followersUsersOptions, setFollowersUsersOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [ticket, setTicket] = useState<FormDataZendeskTicketRequest>(
    new FormDataZendeskTicketRequest({
      typology: null,
      subTypology: null,
      subject: "",
      description: "",
      techInfos: "",
      objectId: -500,
      priority: IssuePriority.Medium,
      followers: [],
    })
  );

  useEffect(() => {
    supportService.GetZendeskFormFields().then((response) => {
      setJsonObject(response.data!);
    });
  }, []);

  useEffect(() => {
    userService.GetAllEmailsAsStringList().then((emails) => {
      if (emails) setFollowersUsersOptions(emails);
    });
  }, []);

  const handleFirstFieldChange = (newValue) => {
    setTypologyValue(newValue);
    setSubTypologyOptions(newValue ? jsonObject?.["Sous-typologie " + newValue.key] : []);
    setSubTypologyValue(null);
  };

  const handleSubmit = useCallback(() => {
    setLoading(true);

    let requestData = {
      ...ticket,
      typology: typologyValue!.value,
      subTypology: subTypologyValue?.value ?? null,
    };

    supportService.CreateZendeskIssue(requestData, fileData).then((response) => {
      if (response.success) {
        closeModal && closeModal();
        customSnackbar(t("alert:success.ticketCreated"), "success");
      }
    });
  }, [ticket, fileData]);

  return jsonObject ? (
    <Modal
      title={t("modal:title.createSupportTicket")}
      closeModal={closeModal}
      width={800}
      reference={ref}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button color="success" disabled={!isValid || loading} onClick={() => handleSubmit()}>
            {t("common:submit")}
          </Button>
          <Button variant="outlined" onClick={closeModal}>
            {t("common:close")}
          </Button>
        </Stack>
      }
    >
      <Box>
        <FormSection
          defaultExpanded={true}
          title={t("common:description")}
          icon={<PublicIcon />}
          sx={{ mb: 2 }}
          size="small"
        >
          <Stack spacing={2}>
            <FormAutocomplete
              multiple
              freeSolo
              popupIcon={<ExpandMoreRoundedIcon />}
              options={followersUsersOptions}
              placeholder={t("field:placeholder.selectFollowers")}
              label={t("field:label.setFollowers")}
              value={ticket.followers}
              onChange={(v) => {
                if (v.length > 0 && !IsEmail(v[v.length - 1])) return;

                setTicket(
                  produce((draft) => {
                    draft.followers = v ?? [];
                  })
                );
              }}
              validationProps={{
                formId: formId,
                formSectionId: "Emails",
                fieldId: "Emails",
                required: false,
                initialState: false,
              }}
            />
            <FormAutocomplete
              popupIcon={<ExpandMoreRoundedIcon />}
              fullWidth
              options={jsonObject?.[GlobalConst.ZENDESK_TYPOLOGY_FIELD_NAME]}
              label={t("common:typology")}
              placeholder={t("field:placeholder.selectTypology")}
              value={typologyValue}
              onChange={handleFirstFieldChange}
              getOptionLabel={(value: any) => {
                return value.key;
              }}
              validationProps={{
                formId: formId,
                formSectionId: "Typology",
                fieldId: "Typology",
                required: true,
                initialState: false,
              }}
            />
            {typologyValue && subTypologyOptions?.length !== undefined && subTypologyOptions?.length > 0 && (
              <FormAutocomplete
                popupIcon={<ExpandMoreRoundedIcon />}
                fullWidth
                options={subTypologyOptions || []}
                label={t("common:subTypology")}
                placeholder={t("field:placeholder.selectSubTypology")}
                value={subTypologyValue}
                onChange={setSubTypologyValue}
                getOptionLabel={(value: any) => {
                  return value.key;
                }}
                validationProps={{
                  formId: formId,
                  formSectionId: "SubTypology",
                  fieldId: "SubTypology",
                  required: true,
                  initialState: false,
                }}
              />
            )}
            <FormAutocomplete
              popupIcon={<ExpandMoreRoundedIcon />}
              fullWidth
              options={Object.keys(IssuePriority)}
              label={t("common:priority")}
              value={ticket.priority}
              onChange={(value) =>
                setTicket(
                  produce((draft) => {
                    draft.priority = value;
                  })
                )
              }
              validationProps={{
                formId: formId,
                formSectionId: "Priority",
                fieldId: "Priority",
                required: false,
                initialState: false,
              }}
            />
            <FormInputText
              label={t("field:label.subject")}
              fullWidth={true}
              value={ticket.subject || ""}
              placeholder={t("field:placeholder.principalSubject")}
              onChange={(e) =>
                setTicket(
                  produce((draft) => {
                    draft.subject = e.target.value;
                  })
                )
              }
              onReset={(e) =>
                setTicket(
                  produce((draft) => {
                    draft.subject = null;
                  })
                )
              }
              validationProps={{
                formId: formId,
                formSectionId: "Subject",
                fieldId: "Subject",
                required: true,
                initialState: false,
                minLength: 3,
              }}
            />
            <FormInputText
              label={t("common:description")}
              placeholder={t("field:placeholder.requestDescription")}
              value={ticket.description || ""}
              multiline
              rows={5}
              onChange={(e) =>
                setTicket(
                  produce((draft) => {
                    draft.description = e.target.value;
                  })
                )
              }
              onReset={(e) =>
                setTicket(
                  produce((draft) => {
                    draft.description = null;
                  })
                )
              }
              validationProps={{
                formId: formId,
                formSectionId: "description",
                fieldId: "description",
                initialState: false,
                required: true,
                minLength: 10,
              }}
            />
          </Stack>
        </FormSection>
        <FormSection
          defaultExpanded={true}
          title={t("form:section.title.fileAttachments")}
          icon={<AttachFileIcon />}
          sx={{ mb: 2 }}
          size="small"
        >
          <UploadCard
            title={t("card:title.dropTicketFileAttachment")}
            coverRounded
            userHasRightToDelete={true}
            uploadButtonInCard={false}
            context={UploadContext.SupportTicket}
            objectId={0}
            format={{
              id: 1,
              isUploaded: false,
              maxFileSizeMb: 10,
              name: "Document",
              userHasRighToDelete: false,
              acceptsFiles: {
                ["application/octet-stream"]: [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"],
              },
              description: t("card:description.ticketAttachment"),
              isUserDataMandatory: false,
              objectType: ObjectType.Document,
            }}
            onDroppedFiles={(files) => setFileData(files[0])}
          />
        </FormSection>
      </Box>
    </Modal>
  ) : (
    <Stack alignItems={"center"}>
      <CircularProgress />
    </Stack>
  );
});

export default ModalSupportZendeskTicket;
